import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { HISTORY_PAGE_SIZE, QUEUE_FETCH_LIMIT } from "../constants/payoutRescue.constants";
import { PaymentsPayoutService, PayoutStatus } from "../services/payoutRescue.service";
import type { RescueFiltersState, RescueKpis, RescuePayout, RescueTab } from "../types/payoutRescue.types";

/**
 * Session-scoped access latch (mirrors `usePaymentsActivity`): a missing `payments:read` role surfaces as a
 * 401 (the RBAC guard throws Unauthorized, not Forbidden) and every 401 costs a silent `/auth/refresh` +
 * replay inside HttpApiService — after the first denial we stop asking for the rest of the session.
 */
const deniedPrincipals = new Set<string>();

const INITIAL_FILTERS: RescueFiltersState = { search: "", organizationId: null, country: null };

/** Start of the local day, for the "paid today" KPI (`from` filters created_at server-side). */
function startOfTodayIso(): string {
  const day = new Date();
  day.setHours(0, 0, 0, 0);
  return day.toISOString();
}

function matchesClientFilters(payout: RescuePayout, filters: RescueFiltersState, orgName: string): boolean {
  if (filters.organizationId && payout.organization_id !== filters.organizationId) return false;
  if (filters.country && payout.country !== filters.country) return false;
  const query = filters.search.trim().toLowerCase();
  if (!query) return true;
  const beneficiary = payout.destination?.beneficiary_name ?? "";
  return [payout.id, orgName, payout.concept ?? "", beneficiary].some((field) =>
    field.toLowerCase().includes(query),
  );
}

export interface UsePayoutRescueQueueResult {
  tab: RescueTab;
  setTab: (tab: RescueTab) => void;
  filters: RescueFiltersState;
  setSearch: (search: string) => void;
  setOrganizationId: (organizationId: string | null) => void;
  setCountry: (country: string | null) => void;
  historyStatus: PayoutStatus | null;
  setHistoryStatus: (status: PayoutStatus | null) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  /** Rows of the ACTIVE tab, already filtered (and, on pending, re-sorted oldest first). */
  rows: RescuePayout[];
  /** History pagination (1-indexed over the offset-based backend). Unused on the queue tabs. */
  historyPage: number;
  setHistoryPage: (page: number) => void;
  historyTotal: number;
  /** True when the CREATED/PROCESSING queue exceeded the 200-row fetch cap (disclosed, never silent). */
  isQueueTruncated: boolean;
  kpis: RescueKpis;
  isLoading: boolean;
  isRefreshing: boolean;
  hasError: boolean;
  /** False after a 401/403 — the caller renders the no-access view. */
  hasAccess: boolean;
  refresh: () => Promise<void>;
  /**
   * Reconcile the local lists from a dispatch/cancel RESPONSE (the fresh row) — the design mandates no
   * timer-based refetching: action responses are the source of truth for the row they touched.
   */
  applyActionResult: (payout: RescuePayout) => void;
}

/**
 * Data core of the rescue queue. Freshness model (design brief §5): NO polling anywhere — data loads on mount
 * and refreshes only on (a) the manual refresh button, (b) tab change, (c) action responses via
 * `applyActionResult`. The pending/processing queues fetch one ≤200 page each (exception queues) and are
 * filtered/sorted client-side; the history tab is server-paginated.
 *
 * INTERNAL-only surface: the route gates on userTypes: [INTERNAL] via RequireAccess, so no org scoping is
 * applied here (staff lists cross-org by design); the backend still enforces tenancy.
 */
export const usePayoutRescueQueue = (orgNameById: Record<string, string>): UsePayoutRescueQueueResult => {
  const { user } = useAuth();
  const deniedKey = user?.id ?? "anon";

  const [tab, setTabState] = useState<RescueTab>("pending");
  const [filters, setFilters] = useState<RescueFiltersState>(INITIAL_FILTERS);
  const [historyStatus, setHistoryStatusState] = useState<PayoutStatus | null>(null);
  const [historyPage, setHistoryPageState] = useState(1);

  const [pending, setPending] = useState<RescuePayout[]>([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [processing, setProcessing] = useState<RescuePayout[]>([]);
  const [processingTotal, setProcessingTotal] = useState(0);
  const [history, setHistory] = useState<RescuePayout[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [paidToday, setPaidToday] = useState<number | null>(null);
  /** Local KPI adjustments from action responses (a rescued payout paid now counts without refetching). */
  const [paidTodayDelta, setPaidTodayDelta] = useState(0);

  const [refreshNonce, setRefreshNonce] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasAccess, setHasAccess] = useState(() => !deniedPrincipals.has(deniedKey));

  // The latest issued request wins — a stale slow response must never overwrite a newer one.
  const requestSeq = useRef(0);

  const historyQueryKey = useMemo(
    () =>
      [
        tab,
        refreshNonce,
        historyStatus ?? "*",
        filters.organizationId ?? "*",
        filters.country ?? "*",
        historyPage,
      ].join("|"),
    [tab, refreshNonce, historyStatus, filters.organizationId, filters.country, historyPage],
  );
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const activeKey = tab === "history" ? historyQueryKey : `queues|${refreshNonce}`;
  const isLoading = hasAccess && settledKey !== activeKey;

  const fetchAll = useCallback(
    async (options: { background: boolean }) => {
      if (deniedPrincipals.has(deniedKey)) return;
      const seq = ++requestSeq.current;
      if (options.background) setIsRefreshing(true);
      else setHasError(false);

      try {
        // The queues + KPIs always refresh together (the team that monitors is the team that rescues);
        // the history page joins only while its tab is active.
        const [createdRes, processingRes, paidRes, historyRes] = await Promise.all([
          PaymentsPayoutService.listPayouts({ status: PayoutStatus.CREATED, limit: QUEUE_FETCH_LIMIT }),
          PaymentsPayoutService.listPayouts({ status: PayoutStatus.PROCESSING, limit: QUEUE_FETCH_LIMIT }),
          PaymentsPayoutService.listPayouts({ status: PayoutStatus.PAID, from: startOfTodayIso(), limit: 1 }),
          tab === "history"
            ? PaymentsPayoutService.listPayouts({
                status: historyStatus ?? undefined,
                organizationId: filters.organizationId,
                country: filters.country ?? undefined,
                limit: HISTORY_PAGE_SIZE,
                skip: (historyPage - 1) * HISTORY_PAGE_SIZE,
              })
            : Promise.resolve(null),
        ]);

        if (seq !== requestSeq.current) return;
        setPending(createdRes.data.data);
        setPendingTotal(createdRes.data.total);
        setProcessing(processingRes.data.data);
        setProcessingTotal(processingRes.data.total);
        setPaidToday(paidRes.data.total);
        setPaidTodayDelta(0);
        if (historyRes) {
          setHistory(historyRes.data.data);
          setHistoryTotal(historyRes.data.total);
        }
        setHasError(false);
        setSettledKey(tab === "history" ? historyQueryKey : `queues|${refreshNonce}`);
      } catch (err: unknown) {
        if (seq !== requestSeq.current) return;
        if (HttpApiService.isAccessDeniedError(err)) {
          deniedPrincipals.add(deniedKey);
          setHasAccess(false);
        } else if (!options.background) {
          // Money display rule: a failed read shows the error state, never an empty list as truth.
          setHasError(true);
          setSettledKey(tab === "history" ? historyQueryKey : `queues|${refreshNonce}`);
        }
        // Background refresh failures keep the last known-good data silently.
      } finally {
        if (seq === requestSeq.current) setIsRefreshing(false);
      }
    },
    [
      deniedKey,
      tab,
      historyStatus,
      filters.organizationId,
      filters.country,
      historyPage,
      historyQueryKey,
      refreshNonce,
    ],
  );

  // Load on mount and on every query change (tab, history filters/page, manual refresh nonce). This is the
  // ONLY automatic fetch path — there is deliberately no interval and no websocket here.
  useEffect(() => {
    void fetchAll({ background: false });
  }, [fetchAll]);

  // Keep the history page in range when the dataset shrinks under us.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));
    if (historyPage > maxPage) setHistoryPageState(maxPage);
  }, [historyPage, historyTotal]);

  const setTab = useCallback((next: RescueTab) => {
    setTabState(next);
    setHistoryPageState(1);
    // Tab change is one of the three sanctioned refresh triggers.
    setRefreshNonce((n) => n + 1);
  }, []);

  const setSearch = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search }));
  }, []);
  const setOrganizationId = useCallback((organizationId: string | null) => {
    setFilters((prev) => ({ ...prev, organizationId }));
    setHistoryPageState(1);
  }, []);
  const setCountry = useCallback((country: string | null) => {
    setFilters((prev) => ({ ...prev, country }));
    setHistoryPageState(1);
  }, []);
  const setHistoryStatus = useCallback((status: PayoutStatus | null) => {
    setHistoryStatusState(status);
    setHistoryPageState(1);
  }, []);
  const clearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setHistoryStatusState(null);
    setHistoryPageState(1);
  }, []);

  // Manual refresh swaps data in place (background) — the table never blanks under the operator.
  const refresh = useCallback(() => fetchAll({ background: true }), [fetchAll]);

  const applyActionResult = useCallback((next: RescuePayout) => {
    setPending((prev) => {
      const without = prev.filter((p) => p.id !== next.id);
      if (next.status === PayoutStatus.CREATED) {
        // Still stuck (failed retry) — keep it in the queue with the fresh row data.
        return prev.some((p) => p.id === next.id) ? prev.map((p) => (p.id === next.id ? next : p)) : prev;
      }
      if (without.length !== prev.length) setPendingTotal((t) => Math.max(0, t - 1));
      return without;
    });
    setProcessing((prev) => {
      const without = prev.filter((p) => p.id !== next.id);
      if (next.status === PayoutStatus.PROCESSING) {
        if (without.length === prev.length) setProcessingTotal((t) => t + 1);
        return [next, ...without];
      }
      if (without.length !== prev.length) setProcessingTotal((t) => Math.max(0, t - 1));
      return without;
    });
    if (next.status === PayoutStatus.PAID) setPaidTodayDelta((d) => d + 1);
    // History stays as-loaded — it refreshes on the next manual refresh / tab change by design.
    setHistory((prev) => prev.map((p) => (p.id === next.id ? next : p)));
  }, []);

  const orgName = useCallback((id: string) => orgNameById[id] ?? "", [orgNameById]);

  const rows = useMemo(() => {
    if (tab === "history") {
      // Server-filtered page; only the free-text search is client-side (the API has none).
      return history.filter((p) =>
        matchesClientFilters(
          p,
          { ...filters, organizationId: null, country: null },
          orgName(p.organization_id),
        ),
      );
    }
    const source = tab === "pending" ? pending : processing;
    const filtered = source.filter((p) => matchesClientFilters(p, filters, orgName(p.organization_id)));
    if (tab === "pending") {
      // Queue ordering: oldest CREATED first — age is the SLA driver (the API only orders DESC).
      return [...filtered].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
    }
    return filtered;
  }, [tab, history, pending, processing, filters, orgName]);

  const kpis = useMemo<RescueKpis>(() => {
    // Retained = blocked funds. The wallet blocks the GROSS amount on create, so the KPI sums gross (what
    // is actually held), not net.
    const retainedByCurrency: Record<string, number> = {};
    for (const p of pending) {
      const amount = Number(p.gross_amount);
      if (!Number.isFinite(amount)) continue;
      retainedByCurrency[p.currency] = (retainedByCurrency[p.currency] ?? 0) + amount;
    }
    return {
      pending: pendingTotal,
      processing: processingTotal,
      paidToday: paidToday === null ? null : paidToday + paidTodayDelta,
      retainedByCurrency,
    };
  }, [pending, pendingTotal, processingTotal, paidToday, paidTodayDelta]);

  return {
    tab,
    setTab,
    filters,
    setSearch,
    setOrganizationId,
    setCountry,
    historyStatus,
    setHistoryStatus,
    clearFilters,
    hasActiveFilters:
      filters.search.trim() !== "" ||
      filters.organizationId !== null ||
      filters.country !== null ||
      historyStatus !== null,
    rows,
    historyPage,
    setHistoryPage: setHistoryPageState,
    historyTotal,
    isQueueTruncated:
      (tab === "pending" && pendingTotal > pending.length) ||
      (tab === "processing" && processingTotal > processing.length),
    kpis,
    isLoading,
    isRefreshing,
    hasError,
    hasAccess,
    refresh,
    applyActionResult,
  };
};
